"use client"

import { useState, useEffect, useRef, Suspense } from "react"
import { useSearchParams } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { ChevronRight, Waves } from "lucide-react"

const questions = [
  {
    id: 1,
    q: "지친 평일을 보낸 당신, 꿀 같은 주말이 찾아왔다면?",
    a: "친구 만나거나 핫플로 나간다.",
    b: "암막 커튼 치고 하루 종일 꿀잠.",
    weight: 15,
  },
  {
    id: 2,
    q: "친구들과 시끌벅적 떠들고 집에 돌아오는 길, 내 기분은?",
    a: "즐거웠던 대화를 곱씹으며 웃는다.",
    b: "갑자기 찾아온 공허함에 한숨이 나온다.",
    weight: 5,
  },
  {
    id: 3,
    q: "고민이 생겼을 때, 나는 어떻게 해결하는 편인가요?",
    a: "주변 사람들에게 털어놓고 위로받는다.",
    b: "혼자만의 동굴로 들어간다.",
    weight: 15,
  },
  {
    id: 4,
    q: "멍하니 바라보고 있을 때 더 마음이 편안한 풍경은?",
    a: "탁 트인 초록색 잔디밭",
    b: "아무 소리도 없는 캄캄한 우주",
    weight: 15,
  },
  {
    id: 5,
    q: "누군가 '요즘 별일 없지?'라고 물어본다면?",
    a: "'응! 완전 좋아~' (진짜 별일 없음)",
    b: "'응, 뭐 똑같지...' (설명하기 귀찮음)",
    weight: 15,
  },
  {
    id: 6,
    q: "이어폰 없이 시끄러운 지하철에 탔을 때 나는?",
    a: "사람 구경도 하고 광고판도 본다.",
    b: "소음 자체가 스트레스다.",
    weight: 5,
  },
  {
    id: 7,
    q: "나는 나의 '감정'을 얼마나 자주 들여다보나요?",
    a: "그때그때 표현해서 쌓아두지 않는다.",
    b: "겉으론 쿨해 보이지만 속은 복잡하다.",
    weight: 5,
  },
  {
    id: 8,
    q: "낯선 사람들과 섞여야 하는 파티에 초대받았다면?",
    a: "설레는 마음으로 꾸미고 나간다.",
    b: "나가기 직전까지 핑계 댈 거리를 찾는다.",
    weight: 5,
  },
  {
    id: 9,
    q: "지금 당장 떠날 수 있다면 어디로 가고 싶나요?",
    a: "화려한 도심 여행",
    b: "핸드폰 안 터지는 깊은 숲속",
    weight: 5,
  },
  {
    id: 10,
    q: "지금 당신에게 가장 필요한 단 한 가지는?",
    a: "짜릿한 '자극'과 '재미'",
    b: "완벽한 '침묵'과 '고요'",
    weight: 15,
  },
]

const results: Record<number, { title: string; desc: string; cta: string }> = {
  0: {
    title: "수심 0m: 투명한 유리구슬",
    desc: "스트레스 제로! 불순물 없이 투명한 멘탈의 소유자입니다. 남들은 당신을 보면 기분이 좋아진다고 해요.",
    cta: "📸 인생샷 건지는 체험 (초보 환영)",
  },
  5: {
    title: "수심 5m: 찰랑이는 발목",
    desc: "잔잔한 파도처럼 평온하네요. 가끔 짜증 나도 맛있는 거 먹고 자면 금방 회복되는 긍정왕!",
    cta: "📸 인생샷 건지는 체험 (초보 환영)",
  },
  10: {
    title: "수심 10m: 스노클링 구간",
    desc: "수면 위를 둥둥 떠다니며 세상 구경하는 걸 좋아하시네요. 호기심 많은 모험가 타입!",
    cta: "📸 인생샷 건지는 체험 (초보 환영)",
  },
  15: {
    title: "수심 15m: 얕은 산호초",
    desc: "겉으로는 마냥 밝아 보이지만, 들여다보면 당신만의 확고한 기준이 숨어 있습니다.",
    cta: "📸 인생샷 건지는 체험 (초보 환영)",
  },
  20: {
    title: "수심 20m: 햇살이 닿는 곳",
    desc: "사회생활 만렙! 하지만 내 시간도 중요하게 챙길 줄 아는 똑똑한 밸런스 능력자.",
    cta: "📸 인생샷 건지는 체험 (초보 환영)",
  },
  25: {
    title: "수심 25m: 다이버의 입구",
    desc: "겉으론 웃고 있지만 속으론 '집에 가고 싶다'는 생각 중? 사회적 가면을 잠깐 벗고 싶군요.",
    cta: "🐬 일상 리프레시! 취미 시작하기",
  },
  30: {
    title: "수심 30m: 아련한 노을 바다",
    desc: "요즘 부쩍 감수성이 풍부해졌어요. 노을을 보면 멍해지거나 옛날 노래가 들리진 않나요?",
    cta: "🐬 일상 리프레시! 취미 시작하기",
  },
  35: {
    title: "수심 35m: 흐릿한 경계선",
    desc: "남에게 맞춰주느라 정작 내가 뭘 좋아하는지 잊어가고 있어요. 배려심이 깊은 만큼 기가 빨리는 중.",
    cta: "🐬 일상 리프레시! 취미 시작하기",
  },
  40: {
    title: "수심 40m: 고요의 시작",
    desc: "시끌벅적한 술자리보다는 마음 맞는 친구 한 명, 혹은 혼자 있는 시간이 편해지기 시작했습니다.",
    cta: "🐬 일상 리프레시! 취미 시작하기",
  },
  45: {
    title: "수심 45m: 푸른 그림자",
    desc: "생각이 꼬리에 꼬리를 물고 이어지네요. 자기 전까지 걱정 시뮬레이션을 돌리느라 뇌가 바쁩니다.",
    cta: "🐬 일상 리프레시! 취미 시작하기",
  },
  50: {
    title: "수심 50m: 중간 지점",
    desc: "놀고는 싶지만 막상 나가면 기 빨리는 상태. 지금 당신의 배터리는 '저전력 모드'입니다.",
    cta: "🧘‍♂️ 잡생각 사라지는 물멍 때리기",
  },
  55: {
    title: "수심 55m: 짙은 남색 물",
    desc: "섣부른 위로보다 침묵이 편안합니다. 내 마음 설명하기도 귀찮고, 그냥 아무 말 없이 있고 싶어요.",
    cta: "🧘‍♂️ 잡생각 사라지는 물멍 때리기",
  },
  60: {
    title: "수심 60m: 해질녘 심해",
    desc: "혼자만의 동굴을 깊게 파고 들어갔군요. 겉으론 티 안 내지만 속으론 많은 걸 참아내고 있습니다.",
    cta: "🧘‍♂️ 잡생각 사라지는 물멍 때리기",
  },
  65: {
    title: "수심 65m: 고요한 동굴",
    desc: "카톡 알림 소리조차 소음일 때가 있죠? 세상의 볼륨을 '음소거' 하고 싶다는 생각을 자주 하시네요.",
    cta: "🧘‍♂️ 잡생각 사라지는 물멍 때리기",
  },
  70: {
    title: "수심 70m: 잠수함 모드",
    desc: "'잠수탄다'는 말이 딱! 연락 다 끊고 아무도 모르는 곳으로 사라지고 싶은 충동이 가득합니다.",
    cta: "🧘‍♂️ 잡생각 사라지는 물멍 때리기",
  },
  75: {
    title: "수심 75m: 심해를 유영하는 고래",
    desc: "거대한 바다를 유영하는 고래처럼, 남들보다 깊고 넓은 마음의 그릇을 가진 듬직한 분이시군요.",
    cta: "🐋 나만의 신비로운 우주 유영하기",
  },
  80: {
    title: "수심 80m: 별이 잠든 밤바다",
    desc: "화려한 네온사인보다 은은한 달빛을 닮았습니다. 침묵의 가치를 아는 당신의 내면엔 우주가 숨어있어요.",
    cta: "🐋 나만의 신비로운 우주 유영하기",
  },
  85: {
    title: "수심 85m: 고요한 독서실",
    desc: "복잡한 세상 속에서 중심을 잃지 않는 단단한 사람. 오롯이 나에게 집중할 줄 아는 힘을 가졌습니다.",
    cta: "🐋 나만의 신비로운 우주 유영하기",
  },
  90: {
    title: "수심 90m: 신비로운 심연",
    desc: "남들은 못 보는 걸 보는 통찰력이 있네요. 멍하니 있는 것 같지만 머릿속에선 창의적인 우주가 펼쳐지는 중!",
    cta: "🌌 중력이 없는 곳으로 여행 떠나기",
  },
  95: {
    title: "수심 95m: 중력을 거스르는 곳",
    desc: "땅 위에서의 삶이 무겁게 느껴진다면, 당신의 영혼이 더 자유로운 곳을 갈망하고 있기 때문입니다.",
    cta: "🌌 중력이 없는 곳으로 여행 떠나기",
  },
  100: {
    title: "수심 100m: 미지의 푸른 우주",
    desc: "아직 발견되지 않은 심해처럼 무한한 잠재력을 품고 있군요. 알면 알수록 매력적인 '볼매' 스타일!",
    cta: "🌌 중력이 없는 곳으로 여행 떠나기",
  },
}

type Screen = "start" | "quiz" | "loading" | "result"

// 부유하는 버블 배경 컴포넌트 (시작 화면용)
const FloatingBubbles = () => {
  const bubbles = [
    { size: 12, left: "10%", delay: 0, duration: 6 },
    { size: 8, left: "25%", delay: 1, duration: 8 },
    { size: 16, left: "40%", delay: 0.5, duration: 7 },
    { size: 10, left: "55%", delay: 1.5, duration: 9 },
    { size: 14, left: "70%", delay: 0.3, duration: 6.5 },
    { size: 6, left: "82%", delay: 2, duration: 8.5 },
    { size: 18, left: "90%", delay: 0.8, duration: 7.5 },
  ]
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {bubbles.map((b, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-white/20 border border-white/30"
          style={{ width: b.size, height: b.size, left: b.left, bottom: "-20px" }}
          animate={{ y: [0, -500], opacity: [0, 0.7, 0] }}
          transition={{ duration: b.duration, repeat: Infinity, delay: b.delay, ease: "easeOut" }}
        />
      ))}
    </div>
  )
}

function DepthTestContent() {
  const searchParams = useSearchParams()
  const [screen, setScreen] = useState<Screen>("start")
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [score, setScore] = useState(0)
  const [finalDepth, setFinalDepth] = useState(0)
  const [copied, setCopied] = useState(false)
  const [displayDepth, setDisplayDepth] = useState(0)
  const counterRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // URL 파라미터로 결과 페이지 바로 표시
  useEffect(() => {
    const resultParam = searchParams.get('result')
    if (resultParam) {
      const depth = parseInt(resultParam, 10)
      if (!isNaN(depth) && depth >= 0 && depth <= 100) {
        setFinalDepth(depth)
        setScreen("result")
      }
    }
  }, [searchParams])

  // 로딩 화면에서 수심 카운터 애니메이션
  useEffect(() => {
    if (screen === "loading") {
      setDisplayDepth(0)
      let current = 0
      const step = Math.max(1, Math.ceil(finalDepth / 40))
      counterRef.current = setInterval(() => {
        current = Math.min(current + step, finalDepth)
        setDisplayDepth(current)
        if (current >= finalDepth) {
          if (counterRef.current) clearInterval(counterRef.current)
        }
      }, 50)
    }
    return () => {
      if (counterRef.current) clearInterval(counterRef.current)
    }
  }, [screen, finalDepth])

  const handleStart = () => {
    setScreen("quiz")
    setCurrentQuestion(0)
    setScore(0)
  }

  const handleAnswer = (isB: boolean) => {
    const newScore = isB ? score + questions[currentQuestion].weight : score

    if (currentQuestion < questions.length - 1) {
      setScore(newScore)
      setCurrentQuestion(currentQuestion + 1)
    } else {
      setScore(newScore)
      setScreen("loading")

      // Calculate final depth (round to nearest 5)
      const depth = Math.round(newScore / 5) * 5
      setFinalDepth(Math.min(100, depth))

      setTimeout(() => {
        setScreen("result")
      }, 2500)
    }
  }

  const handleRestart = () => {
    setScreen("start")
    setCurrentQuestion(0)
    setScore(0)
    setFinalDepth(0)
  }

  const shareToKakao = () => {
    const result = results[finalDepth] || results[100]
    const resultUrl = typeof window !== 'undefined' ? `${window.location.origin}/depth-test?result=${finalDepth}` : ''

    if (typeof window !== 'undefined' && (window as any).Kakao) {
      (window as any).Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: '🌊 내 마음의 수심(Depth) 테스트',
          description: `${result.title}\n\n${result.desc}`,
          imageUrl: 'https://todaydiving.com/og-image.png',
          link: {
            mobileWebUrl: resultUrl,
            webUrl: resultUrl,
          },
        },
        buttons: [
          {
            title: '내 결과 보기',
            link: {
              mobileWebUrl: resultUrl,
              webUrl: resultUrl,
            },
          },
          {
            title: '나도 테스트하기',
            link: {
              mobileWebUrl: `${window.location.origin}/depth-test`,
              webUrl: `${window.location.origin}/depth-test`,
            },
          },
        ],
      })
    } else {
      // 카카오 미초기화 시 클립보드 복사
      navigator.clipboard.writeText(resultUrl).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }).catch(() => {})
    }
  }

  const shareToInstagram = async () => {
    const result = results[finalDepth] || results[100]
    const resultUrl = typeof window !== 'undefined' ? `${window.location.origin}/depth-test?result=${finalDepth}` : ''

    // 인스타그램 스토리 공유 텍스트
    const shareText = `내 마음의 수심은 "${result.title}"!\n\n${result.desc}\n\n나도 테스트하러 가기 👇\n${resultUrl}`

    // 모바일에서 Web Share API 사용
    if (navigator.share) {
      try {
        await navigator.share({
          title: '🌊 내 마음의 수심 테스트',
          text: shareText,
          url: resultUrl,
        })
      } catch (error) {
        // 사용자가 취소한 경우 무시
        if ((error as Error).name !== 'AbortError') {
          console.error('Share failed:', error)
        }
      }
    } else {
      // 데스크톱에서는 클립보드에 복사
      try {
        await navigator.clipboard.writeText(shareText)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (error) {
        console.error('Copy failed:', error)
      }
    }
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100

  // 현재 질문 번호 기반 예상 수심 힌트 (0~100m 사이 선형)
  const currentDepthHint = Math.round((currentQuestion / questions.length) * 100)

  // Calculate background color based on depth (0m = light blue, 100m = dark navy/black)
  const getBackgroundColor = (depth: number) => {
    const lightness = 85 - depth * 0.75 // 85% at 0m to ~10% at 100m
    const saturation = 60 - depth * 0.3 // More saturated at shallow, less at deep
    return `hsl(200, ${saturation}%, ${lightness}%)`
  }

  const getTextColor = (depth: number) => {
    return depth > 40 ? "#ffffff" : "#0a192f"
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 transition-colors duration-1000 relative"
      style={{
        background:
          screen === "result"
            ? getBackgroundColor(finalDepth)
            : screen === "loading"
              ? "linear-gradient(to bottom, #67b7dc, #1e4976)"
              : "linear-gradient(to bottom, #a8dadc, #457b9d)",
      }}
    >
      <AnimatePresence mode="wait">
        {screen === "start" && (
          <motion.div
            key="start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center max-w-2xl w-full relative"
          >
            {/* 부유하는 버블 배경 */}
            <FloatingBubbles />

            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ duration: 0.6 }} className="mb-4 relative z-10">
              <Waves className="w-14 h-14 mx-auto mb-3 text-[#0a192f]/70" />
              <h1 className="text-2xl md:text-5xl font-bold mb-2 text-[#0a192f] text-balance">
                내 마음의 수심(Depth) 테스트
              </h1>
              <p className="text-sm md:text-lg text-[#0a192f]/70 mb-6 text-pretty">
                겉으로는 웃고 있지만,<br className="md:hidden" />
                당신의 영혼은 어디쯤 잠겨 있나요?
              </p>
            </motion.div>

            <div className="relative z-10">
              <Button
                onClick={handleStart}
                size="lg"
                className="bg-[#0a192f] hover:bg-[#0a192f]/90 text-white px-8 py-4 text-base rounded-full shadow-lg hover:shadow-xl transition-all"
              >
                테스트 시작하기
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {screen === "quiz" && (
          <motion.div
            key="quiz"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="w-full max-w-2xl"
          >
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-[#0a192f]/60">
                  {currentQuestion + 1} / {questions.length}
                </span>
                {/* 현재 수심 미리보기 힌트 */}
                <span className="text-xs font-medium text-[#0a192f]/50 flex items-center gap-1">
                  <Waves className="w-3 h-3" />
                  ~{currentDepthHint}m 구간
                </span>
                <span className="text-xs font-medium text-[#0a192f]/60">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <Card className="p-4 md:p-6 bg-white/95 backdrop-blur-sm shadow-2xl border-0">
              <motion.div
                key={currentQuestion}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h2 className="text-base md:text-2xl font-bold mb-3 md:mb-4 text-[#0a192f] text-balance leading-relaxed">
                  {questions[currentQuestion].q}
                </h2>

                <div className="space-y-2 md:space-y-3">
                  <Button
                    onClick={() => handleAnswer(false)}
                    variant="outline"
                    className="w-full p-3 md:p-4 text-left text-sm md:text-base justify-start hover:bg-[#0a192f] hover:text-white transition-all border-2 border-[#0a192f]/20 hover:border-[#0a192f] rounded-xl"
                  >
                    <span className="font-semibold mr-1.5 md:mr-2">A.</span>
                    <span className="text-pretty leading-snug">{questions[currentQuestion].a}</span>
                  </Button>

                  <Button
                    onClick={() => handleAnswer(true)}
                    variant="outline"
                    className="w-full p-3 md:p-4 text-left text-sm md:text-base justify-start hover:bg-[#0a192f] hover:text-white transition-all border-2 border-[#0a192f]/20 hover:border-[#0a192f] rounded-xl"
                  >
                    <span className="font-semibold mr-1.5 md:mr-2">B.</span>
                    <span className="text-pretty leading-snug">{questions[currentQuestion].b}</span>
                  </Button>
                </div>
              </motion.div>
            </Card>
          </motion.div>
        )}

        {screen === "loading" && (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            className="text-center"
          >
            {/* 버블 파티클 5~8개 */}
            <div className="relative h-32 w-48 mx-auto mb-4">
              {[...Array(7)].map((_, i) => {
                const sizes = [8, 12, 6, 16, 10, 14, 9]
                const lefts = ["15%", "30%", "50%", "65%", "80%", "25%", "70%"]
                const delays = [0, 0.3, 0.6, 0.1, 0.8, 1.2, 0.5]
                return (
                  <motion.div
                    key={i}
                    className="absolute rounded-full bg-white/40 border border-white/60"
                    style={{ width: sizes[i], height: sizes[i], left: lefts[i], bottom: 0 }}
                    animate={{ y: [0, -120], opacity: [0, 0.8, 0], scale: [1, 1.3, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity, delay: delays[i], ease: "easeOut" }}
                  />
                )
              })}
              {/* 수심 카운터 */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-4xl font-bold text-white tabular-nums"
                >
                  {displayDepth}m
                </motion.div>
              </div>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-lg md:text-2xl font-bold text-white"
            >
              잠수 중...
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-sm md:text-base text-white/70 mt-2"
            >
              당신의 마음 깊이를<br className="md:hidden" />
              측정하고 있습니다
            </motion.p>
          </motion.div>
        )}

        {screen === "result" && (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="w-full max-w-2xl text-center"
            style={{ color: getTextColor(finalDepth) }}
          >
            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
              <div className="mb-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring" }}
                  className="text-4xl md:text-7xl font-bold mb-2"
                  style={{ color: getTextColor(finalDepth) }}
                >
                  {finalDepth}m
                </motion.div>
                <Waves className="w-10 md:w-12 h-10 md:h-12 mx-auto opacity-50" style={{ color: getTextColor(finalDepth) }} />
              </div>

              {/* 수심 게이지 바 */}
              <div className="mb-4 px-2">
                <div className="flex justify-between text-xs mb-1 opacity-70" style={{ color: getTextColor(finalDepth) }}>
                  <span>0m</span>
                  <span>수심 게이지</span>
                  <span>100m</span>
                </div>
                <div
                  className="h-3 rounded-full overflow-hidden"
                  style={{ backgroundColor: finalDepth > 40 ? "rgba(255,255,255,0.2)" : "rgba(10,25,47,0.15)" }}
                >
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      background: finalDepth > 40
                        ? "linear-gradient(to right, #67b7dc, #ffffff)"
                        : "linear-gradient(to right, #a8dadc, #1e4976)",
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${finalDepth}%` }}
                    transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                  />
                </div>
                <div className="relative mt-1">
                  <motion.div
                    className="absolute text-xs font-bold"
                    style={{ color: getTextColor(finalDepth), left: `${Math.max(0, Math.min(finalDepth - 2, 95))}%` }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                  >
                    ▲
                  </motion.div>
                </div>
              </div>

              <Card
                className="p-4 md:p-6 mb-3 md:mb-4 border-0 shadow-2xl"
                style={{
                  backgroundColor: finalDepth > 40 ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h2
                  className="text-xl md:text-3xl font-bold mb-2 md:mb-3 text-balance"
                  style={{ color: finalDepth > 40 ? "#ffffff" : "#0a192f" }}
                >
                  {results[finalDepth]?.title || results[100].title}
                </h2>
                <p
                  className="text-sm md:text-lg leading-relaxed text-pretty"
                  style={{ color: finalDepth > 40 ? "rgba(255, 255, 255, 0.9)" : "rgba(10, 25, 47, 0.8)" }}
                >
                  {results[finalDepth]?.desc || results[100].desc}
                </p>
              </Card>

              <div className="space-y-2 md:space-y-3">
                {/* CTA 버튼 강조 */}
                <motion.div
                  animate={{ scale: [1, 1.03, 1] }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                >
                  <Button
                    onClick={() => (window.location.href = "/lesson-match")}
                    size="lg"
                    className="w-full py-3 md:py-4 text-sm md:text-base rounded-xl shadow-lg hover:shadow-xl transition-all"
                    style={{
                      backgroundColor: finalDepth > 40 ? "#ffffff" : "#0a192f",
                      color: finalDepth > 40 ? "#0a192f" : "#ffffff",
                    }}
                  >
                    {results[finalDepth]?.cta || results[100].cta}
                  </Button>
                </motion.div>

                {/* 인라인 복사 피드백 */}
                {copied && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-xs font-medium text-center"
                    style={{ color: finalDepth > 40 ? "#a8dadc" : "#457b9d" }}
                  >
                    링크 복사됨! ✓
                  </motion.p>
                )}

                {/* Share Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={shareToKakao}
                    size="lg"
                    className="py-4 text-sm rounded-xl shadow-lg hover:shadow-xl transition-all bg-yellow-400 hover:bg-yellow-500 text-gray-900"
                  >
                    <span className="mr-1">💬</span>
                    카카오톡 공유
                  </Button>
                  <Button
                    onClick={shareToInstagram}
                    size="lg"
                    className="py-4 text-sm rounded-xl shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white"
                  >
                    <span className="mr-1">📷</span>
                    {copied ? "복사됨! ✓" : "인스타 공유"}
                  </Button>
                </div>

                <Button
                  onClick={handleRestart}
                  variant="outline"
                  size="lg"
                  className="w-full py-4 text-base rounded-xl transition-all bg-transparent"
                  style={{
                    borderColor: getTextColor(finalDepth),
                    color: getTextColor(finalDepth),
                    backgroundColor: "transparent",
                  }}
                >
                  다시 테스트하기
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function DepthTestPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-b from-cyan-100 via-blue-100 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Waves className="w-16 h-16 mx-auto mb-4 text-cyan-500 animate-pulse" />
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    }>
      <DepthTestContent />
    </Suspense>
  )
}
